import { useEffect, useMemo, useState } from "react"
import { MessageCircle, PanelLeftClose, Plus, Search, SquarePen, X } from "lucide-react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  CONVERSATION_STATUS_META,
  type RobinConversation,
} from "./robin-types"

/**
 * The rail's width, in pixels, as three numbers rather than one.
 *
 * `canvas` is what artboard 12a draws, on a 1440 artboard — the starting width,
 * not a fixed one. The rail is a panel in the workspace's resize group, so it
 * shares the window with the thread and the ATS and the reader sets the ratio;
 * these two bounds are what the drag is allowed to do. Below `min` the two-line
 * rows stop reading as threads, and past `max` the rail is a column in its own
 * right rather than a rail beside the work.
 */
export const RAIL_PX = {
  /**
   * One line of title and a timestamp still fit here. Below it the rail is a
   * column of ellipses, which is why dragging past `collapseAt` closes it
   * instead of grinding against a floor that will not move.
   */
  min: 168,
  canvas: 262,
  max: 420,
  /** Drag under this and the rail closes. */
  collapseAt: 132,
} as const

/**
 * The conversation rail, from canvas artboard 12a.
 *
 * Chrome: a filled "New conversation" button, then recents grouped by
 * day. Rows are two-line — title with its time, and the last thing said beneath
 * — so the list reads as a set of ongoing threads rather than a list of names.
 * The selected row carries a teal bar inside its left edge.
 *
 * The canvas rail has no brand header (the wordmark lives in the top bar), no
 * search button, and no collapse control. Chat search is still reachable on
 * Cmd-Shift-K.
 */
export function ConversationSidebar({
  conversations,
  selectedId,
  onSelect,
  onNewChat,
  onCollapse,
}: {
  conversations: RobinConversation[]
  selectedId: string
  onSelect: (id: string) => void
  onNewChat: () => void
  /** Absent on a phone, where the rail is a sheet and closes by its scrim. */
  onCollapse?: () => void
}) {
  const [searchOpen, setSearchOpen] = useState(false)

  // ⌘⇧K, not ⌘K. Plain ⌘K opens the ATS command palette (`top-nav.tsx`), and
  // both listeners used to fire on the same chord — opening the palette and this
  // dialog simultaneously.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === "k" &&
        event.shiftKey &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        setSearchOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const visibleConversations = useMemo(
    () => conversations.filter((conv) => conv.messages.length > 0 || conv.hasHistory),
    [conversations],
  )

  const groupedConversations = groupByRecency(visibleConversations)

  return (
    // Width belongs to whatever mounts the rail — a fluid aside on a desktop,
    // a sheet on a phone — so the rail itself simply fills it.
    <div className="@container flex h-full w-full flex-col">
      <div className="shrink-0 px-4 py-3.5">
        <button
          type="button"
          onClick={onNewChat}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-[12.5px] font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          <Plus className="h-[15px] w-[15px]" />
          New conversation
        </button>

        {/*
          The canvas rail has neither of these, and chat search was on ⌘⇧K
          alone — a shortcut nobody finds by looking. A list you cannot search
          and cannot put away is a list that only gets longer.
        */}
        <div className="mt-2.5 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="inline-flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-[11.5px] text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            <Search className="h-[13px] w-[13px] flex-none" />
            <span className="truncate">Search chats</span>
            <span className="ml-auto hidden font-mono text-[10px] @[220px]:inline">⌘⇧K</span>
          </button>
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              aria-label="Collapse the conversation list"
              title="Collapse the conversation list"
              className="flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
            >
              <PanelLeftClose className="h-[15px] w-[15px]" />
            </button>
          )}
        </div>
      </div>

      <ChatSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        conversations={visibleConversations}
        onSelect={(id) => {
          onSelect(id)
          setSearchOpen(false)
        }}
        onNewChat={() => {
          onNewChat()
          setSearchOpen(false)
        }}
      />

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-4" aria-label="Conversations">
        {groupedConversations.length === 0 ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            Your live conversations will appear here.
          </p>
        ) : (
          groupedConversations.map((group) => (
            <section key={group.label}>
              <h2 className="px-2 pb-2 pt-3.5 text-[11.5px] font-medium uppercase text-muted-foreground">
                {group.label}
              </h2>
              <ul className="space-y-0.5">
                {group.items.map((conv) => (
                  <li key={conv.id}>
                    <ConversationRow
                      conv={conv}
                      isSelected={conv.id === selectedId}
                      onSelect={() => onSelect(conv.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </nav>
    </div>
  )
}

/** The last thing said, which is what makes the row a thread rather than a name. */
function lastSaid(conv: RobinConversation): string | null {
  for (let index = conv.messages.length - 1; index >= 0; index -= 1) {
    const text = conv.messages[index]?.text?.trim()
    if (text) return text
  }
  return null
}

function ConversationRow({
  conv,
  isSelected,
  onSelect,
}: {
  conv: RobinConversation
  isSelected: boolean
  onSelect: () => void
}) {
  const meta = CONVERSATION_STATUS_META[conv.status]
  const attention = needsAttention(conv)
  const preview = lastSaid(conv)

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isSelected ? "true" : undefined}
      className={cn(
        "block w-full rounded-md px-2.5 py-2.5 text-left transition-colors",
        // Canvas: the selected row is a surface with a teal bar inside its left
        // edge — an inset shadow, so it does not shift the text.
        isSelected
          ? "bg-card shadow-[inset_2px_0_0_var(--primary)]"
          : "hover:bg-foreground/[0.04]",
      )}
    >
      <span className="flex items-baseline gap-2">
        {attention && (
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full",
              meta.dotClass,
              meta.pulse && "animate-pulse",
            )}
          />
        )}
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-[12.5px] leading-[1.3]",
            isSelected ? "font-medium" : "font-normal",
          )}
        >
          {conv.title}
        </span>
        <span className="shrink-0 whitespace-nowrap font-mono text-[10.5px] text-muted-foreground">
          {conv.updatedLabel}
        </span>
      </span>
      {preview && (
        <span className="mt-[5px] block truncate text-[11.5px] leading-[1.4] text-muted-foreground">
          {preview}
        </span>
      )}
    </button>
  )
}

function ChatSearchDialog({
  open,
  onOpenChange,
  conversations,
  onSelect,
  onNewChat,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  conversations: RobinConversation[]
  onSelect: (id: string) => void
  onNewChat: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-lg">
        <DialogTitle className="sr-only">Search chats</DialogTitle>
        <DialogDescription className="sr-only">
          Search your live conversations with Robin.
        </DialogDescription>
        <Command>
          <div className="flex items-center border-b px-3">
            <CommandInput placeholder="Search chats..." className="border-0 focus:ring-0" />
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="ml-2 rounded-md p-1 text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
              aria-label="Close search"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <CommandList>
            <CommandEmpty>No chats found.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={onNewChat} value="new-chat">
                <SquarePen className="mr-2 h-4 w-4" />
                New chat
              </CommandItem>
              {conversations.map((conv) => (
                <CommandItem
                  key={conv.id}
                  value={`${conv.title} ${conv.messages.map((message) => message.text ?? "").join(" ")}`}
                  onSelect={() => onSelect(conv.id)}
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  <span className="truncate">{conv.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}

function groupByRecency(
  conversations: RobinConversation[],
): { label: string; items: RobinConversation[] }[] {
  const buckets: { label: string; test: (label: string) => boolean }[] = [
    { label: "Today", test: (label) => /^now$|^\d+\s*m$|^\d+\s*h$/i.test(label) },
    { label: "Yesterday", test: (label) => /yesterday/i.test(label) },
    { label: "Previous 7 days", test: () => true },
  ]

  return buckets
    .map((bucket, index) => ({
      label: bucket.label,
      items: conversations.filter(
        (conv) =>
          buckets.findIndex((candidate) => candidate.test(conv.updatedLabel)) === index,
      ),
    }))
    .filter((group) => group.items.length > 0)
}

function needsAttention(conv: RobinConversation): boolean {
  return conv.status === "working" || conv.status === "needs_you" || conv.status === "failed"
}
